import { IconActivity, IconPlus, IconShield, IconTrendingUp, IconUsers } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatRelativeTime } from "@/lib/admin-utils";
import { authClient } from "@/lib/auth-client";
import { StatsCard } from "./components/charts/stats-card";
import { AdminHeader } from "./components/shared/admin-header";
import { queryKeys } from "@/lib/query-keys";

type RecentActivity = {
  id: string;
  type: "user_created";
  message: string;
  timestamp: Date;
  user: { name: string | null; email: string };
};

// Mock function for recent activity - replace with actual admin API call
async function fetchRecentActivity(): Promise<RecentActivity[]> {
  const response = await authClient.admin.listUsers({
    query: {
      limit: 5,
      sortBy: "createdAt",
      sortDirection: "desc",
    },
  });

  if (!response.data) {
    return [];
  }

  return response.data.users.map(
    (user: { id: string; name: string | null; email: string; createdAt: Date }) => ({
      id: user.id,
      type: "user_created" as const,
      message: `New user ${user.name || user.email} registered`,
      timestamp: user.createdAt,
      user: {
        name: user.name,
        email: user.email,
      },
    }),
  );
}

async function fetchUserStats() {
  const response = await authClient.admin.listUsers({
    query: {
      limit: 1000,
      sortBy: "createdAt",
      sortDirection: "desc",
    },
  });

  if (!response.data) {
    return { totalUsers: 0, activeUsers: 0, bannedUsers: 0, recentSignups: 0 };
  }

  const users = response.data.users;
  const totalUsers = users.length;
  const bannedUsers = users.filter((user: { banned?: boolean | null }) => user.banned).length;
  const activeUsers = totalUsers - bannedUsers;

  const RECENT_SIGNUP_DAYS = 7;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - RECENT_SIGNUP_DAYS);
  const recentSignups = users.filter(
    (user: { createdAt: Date }) => new Date(user.createdAt) > sevenDaysAgo,
  ).length;

  return { totalUsers, activeUsers, bannedUsers, recentSignups };
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { data: userStats } = useQuery({
    queryKey: queryKeys.admin.userStats(),
    queryFn: fetchUserStats,
  });
  const { data: recentActivity, isLoading } = useQuery({
    queryKey: queryKeys.admin.recentActivity(),
    queryFn: fetchRecentActivity,
  });

  return (
    <div className="flex flex-col gap-6 p-6">
      <AdminHeader
        action={
          <Button asChild>
            <Link to="/admin/users">
              <IconUsers className="mr-2 h-4 w-4" />
              {t("admin.users.title")}
            </Link>
          </Button>
        }
        description={t("admin.dashboard.description")}
        title={t("admin.dashboard.title")}
      />

      {/* User Stats Cards */}
      {userStats && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            description={t("admin.analytics.totalUsersDesc")}
            icon={<IconUsers className="h-5 w-5" />}
            title={t("admin.analytics.totalUsers")}
            value={userStats.totalUsers.toLocaleString()}
          />
          <StatsCard
            description={t("admin.analytics.activeUsersDesc")}
            icon={<IconActivity className="h-5 w-5" />}
            title={t("admin.analytics.activeUsers")}
            value={userStats.activeUsers.toLocaleString()}
          />
          <StatsCard
            description={t("admin.analytics.newThisWeekDesc")}
            icon={<IconTrendingUp className="h-5 w-5" />}
            title={t("admin.analytics.newThisWeek")}
            value={userStats.recentSignups.toLocaleString()}
          />
          <StatsCard
            description={t("admin.analytics.bannedUsersDesc")}
            icon={<IconShield className="h-5 w-5" />}
            title={t("admin.analytics.bannedUsers")}
            value={userStats.bannedUsers.toLocaleString()}
          />
        </div>
      )}

      {/* Recent Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.dashboard.recentActivity.title")}</CardTitle>
            <CardDescription>{t("admin.dashboard.recentActivity.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }, (_, i) => `loading-${i}`).map((key) => (
                  <div className="flex items-center space-x-3" key={key}>
                    <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
                    <div className="flex-1 space-y-1">
                      <div className="h-4 animate-pulse rounded bg-muted" />
                      <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {recentActivity && recentActivity.length > 0 ? (
                  <div className="space-y-4">
                    {recentActivity.map((activity) => (
                      <div className="flex items-center space-x-3" key={activity.id}>
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                          <IconUsers className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="font-medium text-sm">{activity.message}</p>
                          <p className="text-muted-foreground text-xs">
                            {formatRelativeTime(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {t("admin.dashboard.recentActivity.noActivity")}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("admin.dashboard.quickActions.title")}</CardTitle>
            <CardDescription>{t("admin.dashboard.quickActions.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button asChild className="w-full justify-start" variant="outline">
              <Link to="/admin/users">
                <IconUsers className="mr-2 h-4 w-4" />
                {t("admin.dashboard.quickActions.viewAllUsers")}
              </Link>
            </Button>
            <Button asChild className="w-full justify-start" variant="outline">
              <Link to="/admin/users?action=create">
                <IconPlus className="mr-2 h-4 w-4" />
                {t("admin.dashboard.quickActions.createUser")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
